<script lang="ts">
  import { onMount } from "svelte";
  import {
    listBackups,
    createBackup,
    deleteBackup,
    backupDownloadUrl,
    ApiError,
  } from "../lib/api";
  import { push } from "svelte-spa-router";
  import type { BackupInfo } from "@diy-avatar/shared/admin";

  let backups: BackupInfo[] = [];
  let loading = false;
  let creating = false;
  let error: string | null = null;

  function handle401(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  function humanSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let val = bytes / 1024;
    let i = 0;
    while (val >= 1024 && i < units.length - 1) {
      val /= 1024;
      i += 1;
    }
    return `${val.toFixed(val >= 10 ? 0 : 1)} ${units[i]}`;
  }

  function fmtDate(ms: number | null): string {
    if (ms === null) return "—";
    return new Date(ms).toISOString().slice(0, 19).replace("T", " ");
  }

  /**
   * Cookie-safe cross-origin download: fetch with credentials so the
   * HttpOnly session cookie is sent, then trigger a blob download. A plain
   * anchor `download` would not reliably send the cookie cross-origin.
   */
  async function download(url: string, filename: string) {
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        if (res.status === 401) {
          push("/");
          return;
        }
        throw new ApiError(`HTTP ${res.status}`, res.status);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      if (handle401(err)) return;
      error = err instanceof Error ? err.message : "Download failed.";
    }
  }

  async function loadBackups() {
    loading = true;
    error = null;
    try {
      const res = await listBackups();
      backups = res.backups;
    } catch (err) {
      if (handle401(err)) return;
      error =
        err instanceof Error ? err.message : "Could not load backups.";
    } finally {
      loading = false;
    }
  }

  async function doCreate() {
    creating = true;
    error = null;
    try {
      await createBackup();
      await loadBackups();
    } catch (err) {
      if (handle401(err)) return;
      error =
        err instanceof Error ? err.message : "Could not create backup.";
    } finally {
      creating = false;
    }
  }

  async function doDelete(b: BackupInfo) {
    if (!confirm(`Delete backup ${b.filename}?`)) return;
    error = null;
    try {
      await deleteBackup(b.filename);
      await loadBackups();
    } catch (err) {
      if (handle401(err)) return;
      error =
        err instanceof Error ? err.message : "Could not delete backup.";
    }
  }

  function doDownload(b: BackupInfo) {
    void download(backupDownloadUrl(b.filename), b.filename);
  }

  onMount(() => {
    void loadBackups();
  });
</script>

<section>
  <h1>Backups</h1>

  <div class="panel" data-testid="backup-section">
    <div class="row">
      <button on:click={doCreate} disabled={creating} data-testid="backup-create">
        {creating ? "Creating…" : "Create backup now"}
      </button>
    </div>
    {#if error}
      <p class="error" data-testid="backup-error">{error}</p>
    {/if}

    <table data-testid="backup-table">
      <thead>
        <tr>
          <th>id</th>
          <th>Created</th>
          <th>Size</th>
          <th>Filename</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each backups as b (b.filename)}
          <tr>
            <td>{b.filename}</td>
            <td>{fmtDate(b.created_at)}</td>
            <td>{humanSize(b.bytes)}</td>
            <td>{b.filename}</td>
            <td class="actions">
              <button on:click={() => doDownload(b)}>Download</button>
              <button on:click={() => doDelete(b)}>Delete</button>
            </td>
          </tr>
        {/each}
        {#if backups.length === 0 && !loading}
          <tr><td colspan="5" class="empty" data-testid="backup-empty">No backups.</td></tr>
        {/if}
      </tbody>
    </table>

    <p class="note" data-testid="backup-note">
      Backups are SQLite-only snapshots, created on demand and automatically
      every 24h, retained for 30 days. For disaster recovery see
      docs/backend/disaster-recovery.md.
    </p>
  </div>
</section>

<style>
  h1 {
    margin: 0 0 1rem;
  }
  .panel {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .row {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 0.5rem;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  button:disabled {
    opacity: 0.6;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    background: #111827;
  }
  th,
  td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th {
    background: #0f172a;
    color: #9ca3af;
  }
  .actions {
    display: flex;
    gap: 0.3rem;
  }
  .empty {
    color: #6b7280;
    text-align: center;
  }
  .error {
    color: #fca5a5;
  }
  .note {
    margin: 0.75rem 0 0;
    color: #9ca3af;
    font-size: 0.8rem;
  }
</style>
